/* HELPERS */

/* CONSTANTS */
import { LONG_CACHE_TIME, MEDIUM_CACHE_TIME, SMALL_CACHE_TIME, X_LONG_CACHE_TIME } from '@/api/constants';
import { t } from '@/config/i18n';
import { generalError } from '@/enums/constants';
import { HttpStatus } from '@/enums/httpStatus';
import { router } from '@/router/index';
/* STORES */
import { useGlobalStore } from '@/stores/global';
/* TYPES */
import type { CacheDuration } from '@/types/config/config.types';
import { useToast } from '@af-lib/vue-ui';

const toast = useToast();

/**
 * Return the next page to call based on a server response
 *
 * @param {Any} lastPageData - Last data coming from the server
 *
 * @returns {number | undefined} - page number or undefined if no next page
 */
export function getNextPageValue(lastPageData) {
  const nextPage = lastPageData.meta.page + 1;
  return nextPage <= lastPageData.meta.last_page ? nextPage : undefined;
}

export function getCacheDuration(type: CacheDuration) {
  const hasCache = useGlobalStore().getHasCache;
  if (!hasCache) {
    return 0;
  } else {
    switch (type) {
      case 'small':
        return SMALL_CACHE_TIME;
      case 'medium':
        return MEDIUM_CACHE_TIME;
      case 'long':
        return LONG_CACHE_TIME;
      case 'x-long':
        return X_LONG_CACHE_TIME;
    }
  }
}

/**
 * Display a generic toaster error when a vue query fail
 */
export function displayGenericErrorToaster(error = t(generalError)) {
  toast.show(error, {
    type: 'error'
  });
}

/**
 * Display a generic warning message
 */
export function displayGenericWarningToaster(message) {
  toast.show(message, {
    type: 'warning'
  });
}

/**
 * Display a generic toaster error when a vue query fail
 */
export function displayGenericSuccessToaster(message = t('general.account.changes.done')) {
  toast.show(message, {
    type: 'success'
  });
}

/**
 * Allows to handle vue query errors - either displays a generic toaster, or redirects to a forbidden page in case of 403 error - only works if response status is given
 * @param {number|undefined} status -  http status received for the API call
 * @param {boolean} redirectForbidden - should the user be redirected to a forbidden page in case of 403
 */
export function handleVueQueryErrors({
  status = undefined,
  redirectForbidden = false,
  redirectNotFound = false
}: {
  status: number | undefined;
  redirectForbidden?: boolean;
  redirectNotFound?: boolean;
}) {
  if (redirectForbidden && status === HttpStatus.FORBIDDEN) {
    router.push({ name: 'forbidden' });
  } else if (redirectNotFound && status === HttpStatus.NOT_FOUND) {
    router.push({ name: 'notfound' });
  } else {
    displayGenericErrorToaster();
  }
}

/**
 * Get a list of page from a infinite vue query, and flatMapIt
 */
export function flatMapIt(pages) {
  const data = pages;

  if (data) {
    return pages.flatMap(page => {
      return page.body;
    });
  } else {
    return [];
  }
}

/**
 * Get CSS var in JS
 * @param name
 * @returns
 */
export function getCssVar(name: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Capitalize a string
 * @param str
 * @returns
 */
export const capitalize = (str: string) => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};
